import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');

export default function NonTimeSheet({ row, year, month, onClose }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!row) return;
    setData(null); setQ('');
    axios.get(`${API_URL}/motivare/nontime`, { ...auth(), params: { year, month, indicativ: row.indicativ } })
      .then((r) => setData(r.data)).catch(() => toast.error('Nu s-au putut încărca AWB-urile'));
  }, [row, year, month]);

  const groups = useMemo(() => {
    const items = (data?.items || []).filter((i) => !q || i.awb.toLowerCase().includes(q.toLowerCase()) || i.indicator.toLowerCase().includes(q.toLowerCase()));
    const g = {};
    items.forEach((i) => { (g[i.indicator || '—'] ||= []).push(i); });
    return Object.entries(g).sort((a, b) => b[1].length - a[1].length);
  }, [data, q]);

  const copyAll = () => {
    navigator.clipboard.writeText(groups.flatMap(([, l]) => l.map((i) => i.awb)).join('\n'));
    toast.success('AWB-uri copiate');
  };

  const total = data?.items?.length || 0;
  const shown = groups.reduce((s, [, l]) => s + l.length, 0);

  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[420px] sm:max-w-[420px] flex flex-col" data-testid="nontime-sheet">
        <SheetHeader>
          <SheetTitle className="text-base">Colete întârziate · {row?.indicativ}</SheetTitle>
          <SheetDescription>
            {row?.name}{data?.level_date ? <> · AWB-urile care au impactat Level-ul în ziua de <b>{fmtD(data.level_date)}</b> (ultima zi din perioada Level)</> : ''}
          </SheetDescription>
        </SheetHeader>
        {!data ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...</div>
        ) : total === 0 ? (
          <div className="text-sm text-slate-500 py-6" data-testid="nontime-empty">Niciun colet întârziat în ziua de Level.</div>
        ) : (
          <>
            <div className="flex items-center gap-2 mt-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-2 top-2" />
                <Input className="h-8 pl-8 text-sm" placeholder="Caută AWB / indicator..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="nontime-search" />
              </div>
              <Button variant="outline" size="sm" onClick={copyAll} data-testid="nontime-copy"><Copy className="w-3.5 h-3.5 mr-1" /> Copiază</Button>
            </div>
            <div className="text-xs text-slate-500 mt-2" data-testid="nontime-count">{shown} din {total} AWB-uri</div>
            <div className="overflow-y-auto flex-1 mt-2 space-y-3 pr-1">
              {groups.map(([ind, list]) => (
                <div key={ind} data-testid={`nontime-group-${ind}`}>
                  <div className="flex items-center justify-between text-xs font-semibold text-slate-700 bg-slate-100 rounded px-2 py-1">
                    <span>{ind}</span><span>{list.length}</span>
                  </div>
                  <ul className="mt-1">
                    {list.map((i, k) => <li key={k} className="font-mono text-xs text-slate-700 px-2 py-0.5 border-b border-slate-100 last:border-0">{i.awb}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
